/**
 * Mikrofon-Recorder für die Voiceover-Spur. Kapselt:
 *  – `getUserMedia` mit Echo-Cancel + Noise-Suppression auf Browser-Default,
 *    damit der Voice-Sound auch dann sauber bleibt, wenn der User mit
 *    Speakern (statt Headphones) das Reel zum Lip-Syncen abspielt.
 *  – `MediaRecorder` (webm/opus) für die rohe Aufnahme.
 *  – einen `AnalyserNode` für ein Live-Pegel-Meter (RMS in 0..1).
 *
 * Designed um beim Recording NICHT mit der Reel-Vorschau zu interferieren:
 *  – MediaRecorder läuft in einem eigenen Browser-Thread,
 *  – wir vermeiden synchrone Decode-Schritte während der Aufnahme,
 *  – die Auswertung des Audio-Levels passiert via `requestAnimationFrame`
 *    im UI-Thread, blockt aber nichts im Audio-Pipeline.
 *
 * Lip-Sync: der Recorder merkt sich beim Start die Player-Zeit (Sekunden),
 * sodass der Composer das Resultat anschließend automatisch an die
 * korrekte Timeline-Position als VO-Segment platzieren kann.
 */

export type RecorderStatus = "idle" | "starting" | "recording" | "stopping" | "error";

export type RecorderState = {
  status: RecorderStatus;
  /** Sekunden seit `start()`. */
  elapsedSec: number;
  /** RMS-Audio-Level (0..1), aktualisiert ~30Hz während der Aufnahme. */
  level: number;
  /** Letzter Fehler, falls `status === "error"`. */
  error: string | null;
  /** Player-Zeit beim Start der Aufnahme (Sekunden). */
  startedAtPlayerSec: number;
};

export type StartOptions = {
  /** Player-Position (Sekunden) im Moment des Starts — wird vom Composer
   *  übergeben, damit das Resultat lip-sync-perfekt an dieser Position
   *  als VO-Segment landet. */
  playerStartSec: number;
};

export type StopResult = {
  /** Roh-Blob (typischerweise audio/webm; opus). */
  rawBlob: Blob;
  /** Mime-Type des Roh-Blobs. */
  rawMime: string;
  /** Tatsächliche Aufnahmedauer in Sekunden (gemessen, nicht aus den Daten). */
  durationSec: number;
  /** Player-Zeit, an der die Aufnahme gestartet wurde. */
  playerStartSec: number;
};

export class VoiceoverRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private rafHandle: number | null = null;
  private startTimeMs = 0;
  private playerStartSec = 0;
  private listeners = new Set<(s: RecorderState) => void>();
  private state: RecorderState = {
    status: "idle",
    elapsedSec: 0,
    level: 0,
    error: null,
    startedAtPlayerSec: 0,
  };

  subscribe(listener: (s: RecorderState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): RecorderState {
    return this.state;
  }

  private setState(patch: Partial<RecorderState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  private static pickMime(): string {
    // Cross-browser-Fallback: Chrome/Edge bevorzugen webm/opus, Safari liefert
    // teils nur audio/mp4. Nimm den ersten unterstützten Container.
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    for (const m of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(m)) {
        return m;
      }
    }
    return ""; // browser default
  }

  async start(opts: StartOptions): Promise<void> {
    if (this.state.status === "recording" || this.state.status === "starting") return;
    this.setState({ status: "starting", error: null, elapsedSec: 0, level: 0 });
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        video: false,
      });
      this.chunks = [];
      this.playerStartSec = opts.playerStartSec;

      // Analyser für Pegel-Meter — separater Pfad, beeinflusst die
      // Aufnahme nicht (recorder bekommt direkt den MediaStream).
      const Ctor = (window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) as
        | typeof AudioContext | undefined;
      if (Ctor) {
        this.audioCtx = new Ctor();
        const source = this.audioCtx.createMediaStreamSource(this.stream);
        const analyser = this.audioCtx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        this.analyser = analyser;
      }

      const mime = VoiceoverRecorder.pickMime();
      this.recorder = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
      this.recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) this.chunks.push(ev.data);
      };

      this.startTimeMs = performance.now();
      this.recorder.start(250); // emit chunks every 250ms — keeps latency low
      this.setState({
        status: "recording",
        elapsedSec: 0,
        startedAtPlayerSec: opts.playerStartSec,
      });
      this.tickLoop();
    } catch (e) {
      this.setState({
        status: "error",
        error: e instanceof Error ? e.message : String(e),
      });
      this.cleanup();
      throw e;
    }
  }

  /** Beendet die Aufnahme und liefert den Roh-Blob (Decoding macht der
   *  Caller). Wird `discard: true` übergeben, wird der aktuelle Recorder
   *  einfach gestoppt und das Resultat verworfen. */
  async stop(): Promise<StopResult | null> {
    const rec = this.recorder;
    if (!rec || rec.state === "inactive") {
      this.cleanup();
      this.setState({ status: "idle" });
      return null;
    }
    this.setState({ status: "stopping" });
    const result: StopResult = await new Promise<StopResult>((resolve) => {
      const finalize = (): void => {
        const blob = new Blob(this.chunks, { type: rec.mimeType || "audio/webm" });
        resolve({
          rawBlob: blob,
          rawMime: rec.mimeType || "audio/webm",
          durationSec: (performance.now() - this.startTimeMs) / 1000,
          playerStartSec: this.playerStartSec,
        });
      };
      rec.addEventListener("stop", finalize, { once: true });
      try {
        rec.stop();
      } catch {
        // Wenn stop() blowt: trotzdem sauber finalisieren.
        finalize();
      }
    });
    this.cleanup();
    this.setState({ status: "idle", elapsedSec: 0, level: 0 });
    return result;
  }

  cancel(): void {
    if (this.recorder && this.recorder.state !== "inactive") {
      try { this.recorder.stop(); } catch { /* ignore */ }
    }
    this.chunks = [];
    this.cleanup();
    this.setState({ status: "idle", elapsedSec: 0, level: 0 });
  }

  private cleanup(): void {
    if (this.rafHandle != null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        try { track.stop(); } catch { /* ignore */ }
      }
    }
    this.stream = null;
    this.recorder = null;
    this.analyser = null;
    if (this.audioCtx) {
      const ac = this.audioCtx;
      this.audioCtx = null;
      void ac.close().catch(() => undefined);
    }
  }

  private tickLoop = (): void => {
    if (this.state.status !== "recording") return;
    const elapsedSec = (performance.now() - this.startTimeMs) / 1000;
    let level = 0;
    if (this.analyser) {
      const buf = new Float32Array(this.analyser.fftSize);
      this.analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      // Visuell ein bisschen pushen — RMS ist sonst sehr gedrückt.
      level = Math.min(1, rms * 3.5);
    }
    this.setState({ elapsedSec, level });
    this.rafHandle = requestAnimationFrame(this.tickLoop);
  };
}

/** Singleton für die ganze App — nur eine Aufnahme zur Zeit erlaubt. */
let SHARED: VoiceoverRecorder | null = null;
export function getSharedRecorder(): VoiceoverRecorder {
  if (!SHARED) SHARED = new VoiceoverRecorder();
  return SHARED;
}
